import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Container from '@common/components/container';
import Card from '@common/components/card';
import { ProfileImage } from '@common/components/profile-image';
import { Icon } from '@common/components/icon';
import Access from '@common/components/access/access';
import type { IconTypes } from '@common/components/icon';
import { IntegrationRedeemCode } from '@modules/core/components/integration-redeem-code';
import { ProfileMemberships } from '@modules/core/components/profile-memberships';
import { PrivacyIndicator } from '@modules/core/components/privacy-indicator';
import UserStatusPreview from '@common/components/user-status/user-status-preview';

// @ts-expect-error
import SvgBackgroundCover from '../../../../../../assets/illustrations/background-cover.svg';

import { SET_USER_STATUS, UNSET_USER_STATUS } from '@modules/core/actions';
import { useAppSelector } from '@common/hooks/redux/use-app-selector';
import { useFormattedUserStatus } from '@modules/core/hooks';
import useUserStatusRemoteUpdateListener from '@common/hooks/use-user-status-remote-update-listener';
import {
  getWhiteLabelConfig,
  selected,
} from '@modules/organisation/selectors/organisation';
import {
  EComponentTypes,
  EPermissions,
  EPrivacyLevels,
  WhiteLabelInvitationMode,
} from '@common/definitions';
import type { ExtendedUser, LoggedUser } from '@common/types/objects';
import { UserProfileTypes } from '@modules/core/definitions';
import { Button } from '@common/components/button';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { useRequest } from '@common/services/api';
import { OverviewSection } from '@common/components/overview';
import { FormIcon } from '@modules/forms/components/form-icon';
import { createEvaluation } from '@modules/performance/action/create-evaluation';
import { useHistory, useParams } from 'react-router';
import { useCanAccess, useDidComponentMount } from '@common/hooks';
import Spinner from '@common/components/spinner';
import SearchBar from '@common/components/search-bar';
import { FormsMeta } from '@modules/performance/types';
import Placeholder from '@common/components/placeholder';

type PersonalInfoRowType = {
  value: string | number | null | undefined;
  icon: IconTypes;
  name: string;
  privacyLevel?: EPrivacyLevels;
};

const PersonalInfoRow = ({
  name,
  value,
  icon,
  privacyLevel,
}: PersonalInfoRowType) => (
  <div className="AboutProfile__PersonalInfoRowWrapper">
    <div className="AboutProfile__PersonalInfoRow">
      <Icon type={icon} size={20} />
      <div>
        <span>{name}</span>
        <span>{value || '-'}</span>
      </div>
    </div>
    {typeof privacyLevel === 'number' && (
      <PrivacyIndicator privacyLevel={privacyLevel} />
    )}
  </div>
);

type AboutProfileProps = {
  user: ExtendedUser | LoggedUser;
  type: UserProfileTypes;
  onEdit?: () => void;
  openHighfiveModel?: () => void;
};

export const AboutProfile = ({
  user,
  type,
  onEdit,
  openHighfiveModel,
}: AboutProfileProps) => {
  const { t } = useTranslation();
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const shouldShowRedeemCode =
    useAppSelector(getWhiteLabelConfig)?.invitation_mode ===
    WhiteLabelInvitationMode.CODE;
  const status = useFormattedUserStatus(user?.scopes?.organisation?.status);
  const loggedUser = useAppSelector((state) => state.loggedUser.user);
  const organisation = useAppSelector(selected);
  const { networkId } = useParams<{ networkId: string }>();
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const history = useHistory();
  const [searchForm, setSearchForm] = useState<string | null>(null);
  const didMount = useDidComponentMount();

  const canViewEvaluationModal = useCanAccess({
    permissions: [
      EPermissions.ORGANISATION_PERFORMANCE_REVIEW_EVALUATIONS_CREATE,
      EPermissions.NETWORK_PERFORMANCE_REVIEW_EVALUATIONS_CREATE,
      EPermissions.ORGANISATION_PERFORMANCE_REVIEW_EVALUATIONS_MANAGEMENT,
      EPermissions.NETWORK_PERFORMANCE_REVIEW_EVALUATIONS_MANAGEMENT,
    ],
    components: EComponentTypes.PERFORMANCE_REVIEWS,
  });

  const canViewExtraInfoProps = useMemo(
    () => ({
      permissions: EPermissions.ORGANISATION_USERS_VIEW,
      networks: user?.scopes?.networks || [],
      networkPermissions: EPermissions.NETWORK_USERS_VIEW,
    }),
    [user?.scopes?.networks]
  );

  useUserStatusRemoteUpdateListener(
    user.id,
    (data: Record<string, any>, dispatch: any, userId: string) => {
      if (data.status) {
        dispatch({
          type: SET_USER_STATUS,
          userId,
          emoji: data.status.emoji,
          text: data.status.text,
          expires_at: data.status.expires_at,
        });
      } else {
        dispatch({ type: UNSET_USER_STATUS, userId });
      }
    }
  );

  const formPayload = useMemo(
    () => ({
      ...(searchForm && searchForm !== '' ? { q: searchForm } : {}),
    }),
    [searchForm]
  );

  const {
 data: evaluationFormsData,
 meta: metaDataForms,
 loading: isLoadingEvaluationForms,
 refetch: refetchEvaluationForms,
 reset: resetEvaluationForms
} = useRequest(
    `/v1/organisations/${organisation.id}/performance/forms`, { useCursor: true, payload: formPayload }
  );

  useEffect(() => {
    if (!didMount) return;
    resetEvaluationForms();
  }, [formPayload, didMount, resetEvaluationForms]);
  const metaForms: FormsMeta = metaDataForms;

  const handleCreateEvaluation = useCallback(
    async (
      formId: string,
      reviewerIds: string[],
      userId: string,
      organisationId: string
    ) => {
      try {
        const data = await createEvaluation(organisationId, userId, formId, {
          reviewerIds,
        });
        history.push(`${basePath}/performance/evaluations/${data.id}`);
        return data;
      } catch (error) {
        console.error('Error creating evaluation:', error);
        throw error;
      }
    },
    [organisation.id]
  );

  const showsearch = useMemo(() => {
    const showSearch = metaForms?.pagination?.total_count > 10 ||
    !!searchForm ||
    !!metaForms?.pagination?.next_cursor ||
    evaluationFormsData?.length > 10 ||
    isLoadingEvaluationForms;
    return showSearch;
  }, [metaForms?.pagination?.total_count, searchForm, metaForms?.pagination?.next_cursor, evaluationFormsData?.length]);

  return (
    <Container name="AboutProfile">
      <Container.Content horizontal>
        <Container.Column>
          <Card containerClassName="AboutProfile__Header">
            <SvgBackgroundCover className="AboutProfile__BackgroundCover" />
            <ProfileImage
              className="AboutProfile__Avatar"
              onEdit={onEdit}
              user={user}
              size={64}
            />

            <div className="AboutProfile__GeneralInfo">
              <span className="AboutProfile__FullName">{user.full_name}</span>
              {status && (
                <UserStatusPreview
                  status={status}
                  className="AboutProfile__Subsection"
                />
              )}
              <Access {...canViewExtraInfoProps} and={!!user.last_active}>
                <span className="AboutProfile__Subsection">
                  {t('core:active_count_ago')}{' '}
                  {moment(user.last_active).fromNow()}
                </span>
              </Access>
            </div>

            <div className="AboutProfile__Stats">
              {openHighfiveModel && user.id !== loggedUser.id && (
                <span onClick={openHighfiveModel}>
                  <Button
                    className="tw-flex tw-justify-center tw-flex-row tw-w-full tw-h-full"
                    icon="highfive"
                  >
                    {t('core:user_detail_given_high_five')}
                  </Button>
                </span>
              )}
              {loggedUser.id === user.id && (
                <span>
                  <b>{user.statistics?.likes_count || 0}</b>
                  {t('core:user_detail_likes_count')}
                </span>
              )}
              <span>
                <b>{user.statistics?.messages_count || 0}</b>
                {t('core:user_detail_messages_count')}
              </span>
            </div>
          </Card>

          <Card containerClassName="AboutProfile__PersonalInfo">
            <Card.Content>
              <div className="AboutProfile__PersonalInfoHeader">
                <span>{t('core:profile_personal_information')}</span>
                {onEdit && (
                  <span onClick={onEdit} role="button">
                    {t('core:user_detail_change_button')}
                  </span>
                )}
              </div>

              <PersonalInfoRow
                value={user.bio}
                icon="chat__filled"
                name={t('core:user_detail_biography')}
              />
              <PersonalInfoRow
                value={user.full_name}
                icon="person__filled"
                name={t('core:user_detail_name')}
              />

              <Access {...canViewExtraInfoProps}>
                <PersonalInfoRow
                  value={user.email}
                  icon="email__filled"
                  name={t('core:user_detail_email')}
                />
              </Access>

              <PersonalInfoRow
                value={
                  user.date_of_birth &&
                  moment(user.date_of_birth).format('DD-MM-YYYY')
                }
                icon="calendar_today__filled"
                name={t('core:user_detail_date_of_birth')}
                // @ts-expect-error
                privacyLevel={user?.settings?.privacy.show_date_of_birth}
              />

              <PersonalInfoRow
                value={user.phone_num}
                icon="phone__filled"
                name={t('core:user_detail_phone_number')}
                // @ts-expect-error
                privacyLevel={user?.settings?.privacy.show_phone_num}
              />

              <Access {...canViewExtraInfoProps}>
                <PersonalInfoRow
                  value={
                    user.scopes.organisation.active_from &&
                    moment(user.scopes.organisation.active_from).format(
                      'DD-MM-YYYY'
                    )
                  }
                  icon="calendar_today__filled"
                  name={t('core:profile_form_date_in_service')}
                />
                <PersonalInfoRow
                  value={
                    user.scopes.organisation.active_until &&
                    moment(user.scopes.organisation.active_until).format(
                      'DD-MM-YYYY'
                    )
                  }
                  icon="calendar_today__filled"
                  name={t('core:profile_form_date_out_of_service')}
                />

                <PersonalInfoRow
                  value={
                    user.created_at &&
                    moment(user.created_at).format('DD-MM-YYYY [at] HH:mm')
                  }
                  icon="calendar_today__filled"
                  name={t('core:profile_date_added')}
                />

                <PersonalInfoRow
                  value={
                    user.scopes.organisation.invited_at &&
                    moment(user.scopes.organisation.invited_at).format(
                      'DD-MM-YYYY [at] HH:mm'
                    )
                  }
                  icon="calendar_today__filled"
                  name={t('core:profile_date_invited')}
                />
                <Access components={EComponentTypes.ONBOARDING}>
                  <PersonalInfoRow
                    value={
                      user.scopes.organisation.onboarding
                        ? String(t('core:profile_access_to_onboarding_yes'))
                        : String(t('core:profile_access_to_onboarding_no'))
                    }
                    icon="dashboard"
                    name={t('core:profile_access_to_onboarding')}
                  />
                </Access>

                <PersonalInfoRow
                  value={user.scopes.organisation.external_id}
                  icon="person__filled"
                  name={t('core:profile_form_unique_id')}
                />
              </Access>
            </Card.Content>
          </Card>
        </Container.Column>

        <Container.SideBar className="AboutProfile__Sidebar">
          {shouldShowRedeemCode && (
            <Card>
              <Card.Content>
                <IntegrationRedeemCode user={user} />
              </Card.Content>
            </Card>
          )}
          {canViewEvaluationModal && loggedUser.id !== user.id && (
            <Card>
              <Card.Content>
                <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                  <span className="tw-text-base tw-font-medium tw-text-neutral-textDarkest">
                    {t('performance:evaluate_employee')}
                  </span>
                  <Button
                    type="inverted"
                    onClick={() => setShowEvaluationModal(true)}
                  >
                    {t('performance:select_evaluation')}
                  </Button>
                </div>
              </Card.Content>
            </Card>
          )}
          <ProfileMemberships user={user} type={type} />
        </Container.SideBar>
      </Container.Content>
      {showEvaluationModal && (
        <ModalWindow
          show={showEvaluationModal}
          size="small"
          onHide={() => setShowEvaluationModal(false)}
          className="EditableModule"
        >
          <ModalContent
            title={t('performance:select_evaluation')}
            onHide={() => setShowEvaluationModal(false)}
            hideConfirm
            hideFooter
          >
            {showsearch && (
            <SearchBar
              placeholder={t('common:search_list_search_placeholder')}
              defaultValue={searchForm || ''}
              filter={false}
              onSearch={setSearchForm}
            />
           )}
            {evaluationFormsData?.map(
              (row: any) => row.status === 'live' && (
              <OverviewSection
                className="tw-flex tw-flex-row tw-items-center tw-gap-3
            hover:tw-bg-gray-100 tw-p-2 tw-rounded-md tw-cursor-pointer"
                onClick={() => {
                      handleCreateEvaluation(
                        row.id,
                        [loggedUser.id],
                        user.id,
                        organisation.id
                      );
                    }}
              >
                <FormIcon form={row} />
                <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter">
                  {row.title}
                </div>
              </OverviewSection>
                )
            )}
            {(!isLoadingEvaluationForms && evaluationFormsData?.length === 0) && (
              <Placeholder title={t('performance:no_evaluation_forms')} />
            )}
            {isLoadingEvaluationForms && (
              <Spinner centered />
            )}
            {!isLoadingEvaluationForms && !!metaForms?.pagination?.next_cursor && (
              <Button
                className="tw-w-full tw-mt-2"
                type="inverted"
                onClick={() => refetchEvaluationForms()}
              >
                {t('common:paginated_list_show_more')}
              </Button>
            )}
          </ModalContent>
        </ModalWindow>
      )}
    </Container>
  );
};
